// Testes de arremesso: SMBT (medicine ball sentado) e USSPT (arremesso sentado unilateral).

const { toNumber, round } = require('./primitives');

const SMBT_THROWS = [1, 2, 3, 4];
const USSPT_THROWS = [1, 2, 3];

// Carga do protocolo de referência do USSPT: 6 lb, que dão 2,72 kg. Os valores normativos
// pressupõem essa bola; com outra carga o número ainda serve para acompanhar o próprio
// atleta, mas não para comparar com a referência. Fica aqui para a tela, o PDF, a
// conclusão e a ACIA citarem o mesmo peso.
const USSPT_REFERENCE_BALL_KG = 2.72;
const USSPT_REFERENCE_BALL_LABEL = '2,72 kg (6 lb)';

// A tela grava o que o avaliador digitou; 2.7 e 2.72 são a mesma bola na prática, então a
// comparação tolera a casa decimal a menos.
function ussptBallWeightIsReference(value) {
    const weight = toNumber(value);
    return weight !== null && Math.abs(weight - USSPT_REFERENCE_BALL_KG) < 0.05;
}

// O SMBT é um arremesso só, com as duas mãos (passe de peito) — não tem lado direito e
// esquerdo. A tela antiga pedia os dois, então as avaliações já salvas têm os campos com
// sufixo `_right`/`_left`; a leitura cai para eles quando o campo sem sufixo não existe,
// para prontuário antigo continuar abrindo com os mesmos números.
function smbtLegacyValue(info, base, suffix) {
    for (const key of [`${base}${suffix}`, `${base}_right${suffix}`, `${base}_left${suffix}`]) {
        const value = toNumber(info[key]);
        if (value !== null) return value;
    }
    return null;
}

function smbtInitial(info) {
    return smbtLegacyValue(info, 'smbt_initial', '');
}

function smbtThrow(info, throwNumber) {
    return smbtLegacyValue(info, 'smbt_throw', `_${throwNumber}`);
}

// O alcance inicial é descontado para isolar o que foi de fato arremessado.
function smbtDifference(info, throwNumber) {
    const initial = smbtInitial(info);
    const throwValue = smbtThrow(info, throwNumber);
    if (initial === null || throwValue === null) return null;

    return round(throwValue - initial);
}

function smbtAverage(info) {
    return smbtMean(SMBT_THROWS.map((throwNumber) => smbtDifference(info, throwNumber)));
}

function smbtBest(info) {
    const values = SMBT_THROWS
        .map((throwNumber) => smbtDifference(info, throwNumber))
        .filter((value) => value !== null);

    return values.length ? Math.max(...values) : null;
}

function smbtMean(values) {
    const valid = (values || []).filter((value) => value !== null && value !== undefined);
    if (!valid.length) return null;

    return round(valid.reduce((total, value) => total + value, 0) / valid.length);
}

// No USSPT o desconto é o comprimento do braço, não o alcance inicial.
function ussptDifference(info, side, throwNumber) {
    const throwValue = toNumber(info[`usspt_throw_${side}_${throwNumber}`]);
    const armSize = toNumber(info[`arm_size_${side}`]);
    if (throwValue === null || armSize === null) return null;

    return round(throwValue - armSize);
}

function ussptAverage(info, side) {
    const values = USSPT_THROWS
        .map((throwNumber) => ussptDifference(info, side, throwNumber))
        .filter((value) => value !== null);
    if (!values.length) return null;

    return round(values.reduce((total, value) => total + value, 0) / values.length);
}

function ussptBest(info, side) {
    const values = USSPT_THROWS
        .map((throwNumber) => ussptDifference(info, side, throwNumber))
        .filter((value) => value !== null);
    if (!values.length) return null;

    return Math.max(...values);
}

// Ajuste alométrico: neutraliza a vantagem de massa corporal no arremesso.
const ALLOMETRIC_EXPONENT = 0.35;

function ussptAllometric(info, side) {
    const averageValue = ussptAverage(info, side);
    const weight = toNumber(info.weight);
    if (averageValue === null || weight === null || weight <= 0) return null;

    return round(averageValue / (weight ** ALLOMETRIC_EXPONENT));
}

function ussptSymmetry(right, left) {
    if (right === null || left === null) return null;

    const highest = Math.max(right, left);
    if (highest <= 0) return null;

    return round((Math.min(right, left) / highest) * 100);
}

module.exports = {
    SMBT_THROWS,
    USSPT_THROWS,
    USSPT_REFERENCE_BALL_KG,
    USSPT_REFERENCE_BALL_LABEL,
    ussptBallWeightIsReference,
    smbtInitial,
    smbtThrow,
    smbtDifference,
    smbtAverage,
    smbtBest,
    smbtMean,
    ussptDifference,
    ussptAverage,
    ussptBest,
    ussptAllometric,
    ussptSymmetry
};
